import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, SafeAreaView, Text, Image, Alert, Modal, TouchableOpacity } from 'react-native';
import { Button, Card, TextInput, Checkbox, useTheme, Divider, Portal, Dialog, Title, Paragraph } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { Dropdown } from 'react-native-element-dropdown';
import { getMyChildren } from '../../api/childrenAPI';
import { createActivity, getMyActiveActivities } from '../../api/activitiesAPI';
import { useFocusEffect } from '@react-navigation/native';

const ParkEntryScreen = ({ navigation }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [entryTime, setEntryTime] = useState(new Date());
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [termsDialogVisible, setTermsDialogVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [selectedChildName, setSelectedChildName] = useState('');
  const [isCreatingActivity, setIsCreatingActivity] = useState(false);
  
  // İptal Modal'ı için state tanımlamaları
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [cancellingActivity, setCancellingActivity] = useState(false);

  // Çocukları API'den getir
  const fetchChildren = async () => {
    try {
      setLoading(true);
      const response = await getMyChildren();
      //console.log('Çocuklar API yanıtı:', JSON.stringify(response, null, 2));
      
      if (response.data) {
        // Çocukları dropdown için düzenle
        const formattedChildren = response.data.map(child => ({
          label: `${child.first_name} ${child.last_name}`,
          value: Number(child.id), // ID'yi sayıya dönüştürüyoruz
          age: calculateAge(child.birthdate),
          gender: child.gender
        }));
        
        //console.log('Dropdown için düzenlenen çocuk listesi:', formattedChildren);
        setChildren(formattedChildren);
      }
    } catch (err) {
      console.error('Çocukları getirirken hata:', err);
      Alert.alert('Hata', 'Çocuk bilgileri yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };
  
  // Sayfa odaklandığında çocukları getir
  useFocusEffect(
    React.useCallback(() => {
      fetchChildren();
      return () => {};
    }, [])
  );
  
  // Yaş hesaplama 
  const calculateAge = (birthdate) => {
    if (!birthdate) return '';
    
    try {
      const today = new Date();
      let birthDate;
      
      if (typeof birthdate === 'string') {
        const parts = birthdate.split('T')[0].split('-');
        if (parts.length === 3) {
          birthDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          birthDate = new Date(birthdate);
        }
      } else {
        birthDate = new Date(birthdate);
      }
      
      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();
      
      if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
        years--;
      }
      
      return `${years} yaş`;
    } catch (error) {
      console.error('Yaş hesaplama hatası:', error);
      return '';
    }
  };
  
  // Dropdown değiştiğinde
  const handleChildChange = (item) => {
    //console.log('Seçilen çocuk:', item);
    setSelectedChild(Number(item.value)); // ID'yi sayıya dönüştürüyoruz
  };

  // Oyun parkı girişi oluştur
  const handleCreateActivity = async () => {
    if (!selectedChild) {
      Alert.alert('Uyarı', 'Lütfen bir çocuk seçin.');
      return;
    }
    
    if (!agreed) {
      Alert.alert('Uyarı', 'Lütfen şartları ve koşulları kabul edin.');
      return;
    }
    
    try {
      setIsCreatingActivity(true);
      
      // Önce aktif aktivite kontrolü yapalım
      const activeActivitiesResponse = await getMyActiveActivities();
      
      if (activeActivitiesResponse.success && activeActivitiesResponse.data && activeActivitiesResponse.data.length > 0) {
        //console.log('Aktif aktiviteler bulundu, kontrol ediliyor...');
        
        // Seçilen çocuğun aktif bir oyun parkı ziyareti var mı kontrol edelim
        // Sayısal karşılaştırma için ID'leri sayı olarak dönüştürüyoruz
        const selectedChildId = Number(selectedChild);
        const hasActiveVisit = activeActivitiesResponse.data.some(activity => {
          // API'den gelen child_id veya activity.child içindeki id alanlarının sayısal değerlerini al
          const activityChildId = activity.child_id ? Number(activity.child_id) : null;
          const childObjId = activity.child && activity.child.id ? Number(activity.child.id) : null;
          
          //console.log(`Aktivite: ${activity.id}, Child ID: ${activityChildId || childObjId}, Seçilen: ${selectedChildId}`);
          
          // ID'ler eşleşiyor mu?
          return (activityChildId === selectedChildId) || (childObjId === selectedChildId);
        });
        
        //console.log('Aktif ziyaret var mı?', hasActiveVisit);
        
        if (hasActiveVisit) {
          // Çocuğun adını kaydet ve uyarı mesajını ayarla
          const childObj = children.find(child => Number(child.value) === selectedChildId);
          const childName = childObj ? childObj.label : "Seçilen çocuk";
          setSelectedChildName(childName);
          setErrorMessage(`${childName} için zaten aktif bir oyun parkı ziyareti bulunmaktadır. Yeni bir giriş yapabilmek için önce mevcut ziyareti tamamlamanız gerekmektedir.`);
          setErrorModalVisible(true);
          setIsCreatingActivity(false);
          return;
        }
      }
      
      // Aktif ziyaret yoksa, onay modalı göster
      const childObj = children.find(child => Number(child.value) === selectedChild);
      const childName = childObj ? childObj.label : "Seçilen çocuk";
      setSelectedChildId(selectedChild);
      setSelectedChildName(childName);
      setConfirmModalVisible(true);
      setIsCreatingActivity(false);
      
    } catch (error) {
      console.error('Oyun parkı girişi kontrol hatası:', error);
      Alert.alert(
        'Hata',
        'Oyun parkı girişi kontrolü yapılamadı: ' + (error.message || 'Bilinmeyen hata'),
        [{ text: 'Tamam' }]
      );
      setIsCreatingActivity(false);
    }
  };

  // Onay sonrası park girişi oluştur
  const createParkEntry = async () => {
    try {
      setIsCreatingActivity(true);
      setConfirmModalVisible(false);
      
      // Oyun parkı giriş kaydı için API isteği
      //console.log('Yeni giriş kaydı oluşturuluyor...');
      const entryData = {
        childId: selectedChildId,
        activityType: 'oyun-parki',
        startTime: entryTime.toISOString(),
        notes: 'Oyun Parkı Girişi'
      };
      
      const response = await createActivity(entryData);
      //console.log('Giriş kaydı oluşturuldu:', response);
      
      // Başarı mesajını göster
      setErrorMessage('Oyun parkı giriş kaydınız başarıyla oluşturuldu!');
      setErrorModalVisible(true);
      
      // Formu sıfırla
      //setSelectedChild(null);
      setAgreed(false);
      
    } catch (error) {
      console.error('Kayıt oluştururken hata:', error);
      
      if (error.message && error.message.includes('Bu çocuk için zaten aktif veya onay bekleyen bir oyun parkı girişi bulunmaktadır')) {
        // Modal ile hata mesajını göster
        setErrorMessage('Bu çocuk için zaten aktif veya onay bekleyen bir oyun parkı girişi bulunmaktadır');
        setErrorModalVisible(true);
      } else {
        // Diğer hatalar için Alert kullan
        Alert.alert(
          'Hata',
          'Kayıt oluşturulurken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'),
          [{ text: 'Tamam' }]
        );
      }
    } finally {
      setIsCreatingActivity(false);
    }
  };

  // Dropdown renderItem fonksiyonu
  const renderDropdownItem = (item) => (
    <View style={styles.dropdownItem}>
      <Text style={styles.dropdownItemText}>{item.label}</Text>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <Modal
        visible={errorModalVisible}
        transparent={true}
        onRequestClose={() => setErrorModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.errorModalContent}>
            <MaterialCommunityIcons 
              name={errorMessage.includes('başarıyla') ? "check-circle-outline" : "alert-circle-outline"}
              size={50} 
              color="rgb(125, 76, 236)"
              style={styles.errorIcon} 
            />
            
            <Text style={[styles.errorModalTitle, errorMessage.includes('başarıyla') && { color: 'rgb(125, 76, 236)' }]}>
              {errorMessage.includes('başarıyla') ? 'Giriş Kaydı Oluşturuldu' : 'Aktif Veya Onay Bekleyen Ziyaret Mevcut'}
            </Text>
            <Text style={styles.errorModalText}>{errorMessage}</Text>
            
            <View style={styles.errorModalButtonContainer}>
              
              {/*
              {errorMessage.includes('başarıyla') ? (
                
                <Button 
                  mode="contained" 
                  onPress={() => {
                    setErrorModalVisible(false);
                    navigation.navigate('Activities', { screen: 'PendingActivities' });
                  }}
                  style={[styles.modalButton, { backgroundColor: '#4CAF50' }]}
                  icon="clock-outline"
                >
                  Onay Bekleyen Ziyaretler
                </Button>
              ) : (
                <Button 
                  mode="contained" 
                  onPress={() => {
                    setErrorModalVisible(false);
                    navigation.navigate('ActiveParkActivities');
                  }}
                  style={[styles.modalButton, { backgroundColor: '#4CAF50' }]}
                  icon="run-fast"
                >
                  Aktif Ziyaretleri Gör
                </Button>
              )}
              */}
                
              <Button 
                mode="outlined" 
                onPress={() => setErrorModalVisible(false)}
                style={styles.modalButton}
              >
                Kapat
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={confirmModalVisible}
        transparent={true}
        onRequestClose={() => setConfirmModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.errorModalContent}>
            <MaterialCommunityIcons 
              name="help-circle-outline" 
              size={50} 
              color={theme.colors.primary}
              style={styles.errorIcon} 
            />
            
            <Text style={styles.errorModalTitle}>Giriş Onayı</Text>
            <Text style={styles.errorModalText}>{selectedChildName} olarak kayıtlı çocuğunuzun oyun alanına giriş talebini onaylıyor musunuz?</Text>
            
            <View style={styles.errorModalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={createParkEntry}
                style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon="check-circle"
                loading={isCreatingActivity}
                disabled={isCreatingActivity}
              >
                Onayla
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setConfirmModalVisible(false)}
                style={styles.modalButton}
              >
                İptal
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      
      {/* Kullanım Şartları ve Kuralları Modal */}
      <Modal
        visible={termsDialogVisible}
        transparent={true}
        onRequestClose={() => setTermsDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.errorModalContent}>
            <MaterialCommunityIcons 
              name="information-outline" 
              size={50} 
              color="rgb(125, 76, 236)"
              style={styles.errorIcon} 
            />
            
            <Text style={styles.errorModalTitle}>OYUN PARKI KULLANIM ŞARTLARI ve KURALLARI</Text>
            
            <View style={styles.termsScrollContainer}>
              <ScrollView contentContainerStyle={{ paddingHorizontal: 5 }}>
                <Text style={styles.errorModalText}>
                  Değerli misafirimiz,
                  Oyun parkı alanımızı güvenli ve keyifli bir şekilde kullanabilmeniz adına aşağıdaki kurallara uymanızı rica ederiz:
                </Text>
                
                <View style={styles.termsDivider} />
                
                <Text style={[styles.errorModalText, { fontWeight: 'bold', color: '#FF9800' }]}>
                  📌 Genel Kurallar
                </Text>
                <Text style={styles.errorModalText}>
                  1.	Oyun alanı yalnızca 2 – 12 yaş arası çocukların kullanımına açıktır.
                </Text>
                <Text style={styles.errorModalText}>
                  2.	Çocuklar oyun alanını ebeveyn gözetimi olmaksızın kullanabilir. Ancak alanın güvenli kullanımı ve doğabilecek tüm sorumluluklar tamamen ebeveynlere veya yasal sorumlulara aittir.
                </Text>
                <Text style={styles.errorModalText}>
                  3.	Oyun alanında yiyecek, içecek tüketmek yasaktır.
                </Text>
                <Text style={styles.errorModalText}>
                  4.	Oyun alanına çorapsız giriş yapılamaz. Çocukların çorap giymesi zorunludur.
                </Text>
                <Text style={styles.errorModalText}>
                  5.	Çocukların üzerine uygun olmayan kıyafet veya kesici/takılı eşyalarla (fermuar, kemer, metal toka vb.) girmesi güvenlik açısından sakıncalıdır.
                </Text>
                
                <View style={styles.termsDivider} />
                
                <Text style={[styles.errorModalText, { fontWeight: 'bold', color: '#FF9800' }]}>
                  ⚠️ Güvenlik ve Sorumluluk
                </Text>
                <Text style={styles.errorModalText}>
                  6.	Çocukların oyun alanında meydana gelebilecek küçük kazalar, çarpma, düşme gibi durumlarda işletme sorumluluk kabul etmez.
                </Text>
                <Text style={styles.errorModalText}>
                  7.	Ebeveyn, çocuğunun davranışlarından ve fiziksel güvenliğinden tamamen kendisi sorumludur.
                </Text>
                <Text style={styles.errorModalText}>
                  8.	Oyun alanında başka çocuklara zarar verilmesi, agresif davranışlar veya kurallara uymama durumlarında personel tarafından alan kullanımı sonlandırılabilir.
                </Text>
                <Text style={styles.errorModalText}>
                  9.	Oyun alanına kişisel oyuncak, elektronik cihaz veya değerli eşya getirilmesi önerilmez. İşletme, kayıp veya zarar gören eşyalar için sorumluluk kabul etmez.
                </Text>
                
                <View style={styles.termsDivider} />
                
                <Text style={[styles.errorModalText, { fontWeight: 'bold' }]}>
                  📣 Bu alanı kullanarak yukarıdaki tüm kuralları okuduğunuzu, anladığınızı ve kabul ettiğinizi beyan etmiş olursunuz.
                </Text>
              </ScrollView>
            </View>
            
            <View style={styles.errorModalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={() => {
                  setAgreed(true);
                  setTermsDialogVisible(false);
                }}
                style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon="check-circle"
              >
                Kabul Ediyorum
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setTermsDialogVisible(false)}
                style={styles.modalButton}
              >
                Kapat
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      <ScrollView style={styles.scrollView}>
        {/* Başlık Kartı */}
        <Card style={styles.headerCard}>
          <Card.Content>
            {/* Butonlar */}
            <View style={[styles.buttonContainer, { flexDirection: 'column' }]}>
              <Button 
                mode="contained"
                onPress={() => navigation.navigate('ParkHistory')}
                style={[styles.actionButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon={({ size, color }) => (
                  <MaterialCommunityIcons name="history" size={size} color="white" />
                )}
                labelStyle={{ color: 'white' }}
              >
                Oyun Parkı Geçmişi
              </Button>
              
              <Button 
                mode="contained"
                onPress={() => navigation.navigate('ActiveParkActivities')}
                style={[styles.actionButton, { marginTop: 10, backgroundColor: 'rgb(125, 76, 236)' }]}
                icon={({ size, color }) => (
                  <MaterialCommunityIcons name="run-fast" size={size} color="white" />
                )}
                labelStyle={{ color: 'white' }}
              >
                Aktif Ziyaretler
              </Button>
            </View>
            
            <Title style={styles.headerTitle}>Oyun Parkı Girişi</Title>
            <Text style={styles.headerSubtitle}>
              Çocuğunuz için oyun parkına giriş kaydı oluşturun
            </Text>
          </Card.Content>
        </Card>
        
        {/* Ana Form */}
        <Card style={styles.formCard}>
          <Card.Content>
            <View style={styles.formSection}>
              <Text style={styles.sectionTitle}>Çocuk Seçimi</Text>
              <Dropdown
                style={styles.dropdown}
                placeholderStyle={styles.placeholderStyle}
                selectedTextStyle={styles.selectedTextStyle}
                data={children}
                maxHeight={300}
                labelField="label"
                valueField="value"
                placeholder="Çocuk Seçin"
                value={selectedChild}
                onChange={handleChildChange}
                renderItem={renderDropdownItem}
                loading={loading}
              />
              {children.length === 0 && !loading && (
                <View style={styles.emptyChildrenContainer}>
                  <Text style={styles.emptyChildrenText}>Henüz kayıtlı çocuk bulunamadı.</Text>
                  <Button 
                    mode="contained" 
                    onPress={() => navigation.navigate('AddChild')}
                    style={styles.addChildButton}
                  >
                    Çocuk Ekle
                  </Button>
                </View>
              )}
            </View>
            
            <Divider style={styles.divider} />
            
            <View style={styles.formSection}>
              <Text style={styles.sectionTitle}>Giriş Bilgileri</Text>
              
              <Text style={styles.inputLabel}>Giriş Tarihi ve Saati</Text>
              <Text style={styles.dateTimeText}>
                {entryTime.toLocaleString('tr-TR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Text>
              
              <Text style={styles.infoTextSmall}>
                Not: Çıkış yapıldığında süre otomatik hesaplanacaktır.
              </Text>
            </View>
            
            <Divider style={styles.divider} />
            
            <View style={styles.formSection}>
              <View style={styles.checkboxContainer}>
                <Checkbox
                  status={agreed ? 'checked' : 'unchecked'}
                  onPress={() => setAgreed(!agreed)}
                  color={theme.colors.primary}
                />
                <Text style={styles.termsText}>
                <Text 
                    style={styles.termsLink}
                    onPress={() => setTermsDialogVisible(true)}
                  >
                    Oyun parkı şartları ve koşullarını
                    
                  </Text>
                  {' '} okudum ve kabul ediyorum.
                  
                  
                </Text>
                
              </View>
            </View>
          </Card.Content>
          
          <Card.Actions style={styles.cardActions}>
            <Button 
              mode="contained"
              onPress={handleCreateActivity}
              disabled={loading || !selectedChild || !agreed}
              loading={isCreatingActivity}
              style={styles.submitButton}
            >
              Giriş Kaydı Oluştur
            </Button>
          </Card.Actions>
        </Card>
        
        {/* Bilgi Kartı */}
        <Card style={styles.infoCard}>
          <Card.Content>
            <View style={styles.infoHeader}>
              <MaterialCommunityIcons name="information-outline" size={24} color={theme.colors.primary} />
              <Text style={styles.infoTitle}>Önemli Bilgiler</Text>
            </View>
            <Text style={styles.infoText}>• Çocukların uygun ayakkabı giymesi gerekmektedir.</Text>
            <Text style={styles.infoText}>• Oyun alanına yiyecek ve içecek sokmak yasaktır.</Text>
            <Text style={styles.infoText}>• 3 yaşından küçük çocuklar için ebeveyn eşliği gereklidir.</Text>
          </Card.Content>
        </Card>
        
        
      </ScrollView>
      
      {/* Başarı iletişim kutusu - Modal ile değiştirildi */}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  scrollArea: {
    maxHeight: 400, // Maksimum yükseklik sınırlaması
    paddingHorizontal: 0,
  },
  termsDialog: {
    maxWidth: '95%',
    width: '95%',
    alignSelf: 'center',
  },
  termsScrollContent: {
    paddingVertical: 10,
    paddingHorizontal: 5,
  },
  termsParagraph: {
    marginBottom: 10,
    fontSize: 14,
    lineHeight: 20,
  },
  termsSectionTitle: {
    fontWeight: 'bold',
    fontSize: 16,
    marginTop: 10,
    marginBottom: 5,
  },
  termsFooter: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 10,
  },
  termsDivider: {
    height: 1,
    backgroundColor: '#ddd',
    marginVertical: 15,
  },
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  scrollView: {
    flex: 1,
    padding: 16,
  },
  headerCard: {
    marginBottom: 16,
    borderRadius: 8,
    elevation: 2,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  formCard: {
    marginBottom: 16,
    borderRadius: 8,
    elevation: 2,
  },
  formSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#333',
  },
  dropdown: {
    height: 50,
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  dropdownItemText: {
    marginLeft: 10,
    fontSize: 14,
  },
  placeholderStyle: {
    fontSize: 14,
    color: '#999',
  },
  selectedTextStyle: {
    fontSize: 14,
  },
  inputLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  dateTimeText: {
    fontSize: 16,
    padding: 12,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  termsText: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    color: '#666',
  },
  termsLink: {
    color: 'rgb(125, 76, 236)',
    textDecorationLine: 'underline',
    fontWeight: 'bold',
  },
  cardActions: {
    justifyContent: 'center',
    paddingTop: 8,
    paddingBottom: 16,
  },
  submitButton: {
    width: '100%',
    paddingVertical: 6,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  infoCard: {
    marginBottom: 16,
    borderRadius: 8,
    elevation: 2,
    backgroundColor: 'rgba(125, 76, 236, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  infoTitle: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  infoText: {
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
    lineHeight: 20,
  },
  infoTextSmall: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 8,
  },
  emptyChildrenContainer: {
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyChildrenText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  addChildButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  buttonContainer: {
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  actionButton: {
    width: '100%',
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
    marginBottom: 10,
  },
  // Modal Stilleri
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalHeader: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(125, 76, 236, 0.3)',
    paddingVertical: 15,
    paddingHorizontal: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  modalBody: {
    paddingVertical: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  modalText: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    lineHeight: 24,
    textAlign: 'center',
    marginTop: 10,
  },
  warningIcon: {
    marginBottom: 10,
  },
  modalFooter: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
    marginHorizontal: 5,
    marginTop: 10,
  },
  singleButton: {
    backgroundColor: 'rgb(125, 76, 236)',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  buttonText: {
    color: 'white',
    fontWeight: '600',
  },
  modalContent: {
    alignItems: 'center',
  },
  dialogText: {
    fontSize: 16,
    color: '#333',
    marginBottom: 16,
  },
  // Modern modal stilleri
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  errorModalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxWidth: 500,
    maxHeight: '90%',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
  },
  errorIcon: {
    marginBottom: 15,
    color: 'rgb(125, 76, 236)',
  },
  errorModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  errorModalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 10,
    color: 'rgb(90, 50, 180)',
  },
  termsScrollContainer: {
    maxHeight: 400,
    width: '100%',
  },
  errorModalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 10,
  },
  modalButton: {
    marginHorizontal: 5,
    borderRadius: 4,
    marginTop: 10,
    flex: 1,
    minWidth: 120,
  },
});

export default ParkEntryScreen;
